import { getCookieUserCoordinates } from '@/features/place/cookies/cookie-user-coordinates';
import {type Coordinates, type Place} from '@/features/place/types/place-types';
import geocodeFilterSearchResults from '@/libs/geocode-earth/geocode-filter-search-results';
import sendHttpError from '@/libs/logger/requests/send-http-error';

import {
  type FeatureResponse,
  type GeocodeEarthResponse,
} from './geocode-earth-types';
import placeAdapter from "@/features/place/requests/adapters/place-adapter";
import { geoDebugLog, hashForGeoDebug } from "@/shared/utils/geo-debug";

const config = {
  apiKey: process.env.NEXT_PUBLIC_GEO_SERVICE_API_KEY,
  autocompleteUrl: 'https://api.geocode.earth/v1/autocomplete',
  forwardUrl: 'https://api.geocode.earth/v1/search',
  reverseUrl: 'https://api.geocode.earth/v1/reverse',
  layersAutocomplete: 'address,street,neighbourhood,locality,borough,county,region,country',
  layersSearch: 'address,street,neighbourhood,borough,localadmin,locality,county,macrocounty,region,macroregion,country',
  sources: 'openaddresses,openstreetmap,whosonfirst',
};

const geocodeEarthRequestParameters: Record<string, string> = {
  api_key: config.apiKey,
  lang: 'en',
  sources: config.sources,
};

const geocodeEarthAutocompleteParameters: Record<string, string> = {
  ...geocodeEarthRequestParameters,
  layers: config.layersAutocomplete,
  size: '10',
};

const geocodeEarthSearchParameters: Record<string, string> = {
  ...geocodeEarthRequestParameters,
  layers: config.layersSearch,
};

export async function autocompleteByName(searchPlaceName: string) {
  const requestId = `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
  const userCoordinates = getCookieUserCoordinates();

  // First attempt: try WITH focus point (local/nearby results)
  if (userCoordinates) {
    const localRequestParameters: Record<string, string> = {
      ...geocodeEarthAutocompleteParameters,
      text: searchPlaceName,
      'focus.point.lat': String(userCoordinates.latitude),
      'focus.point.lon': String(userCoordinates.longitude),
    };

    const localUrlParameters = new URLSearchParams(localRequestParameters).toString();
    const localGeocoderUrl = `${config.autocompleteUrl}?${localUrlParameters}`;

    geoDebugLog('geocoder_request_start', {
      endpoint: 'autocomplete',
      request_id: requestId,
      text_hash: hashForGeoDebug(searchPlaceName),
      has_focus_point: true,
      attempt: 'local',
    });

    const localResult = await fetch(localGeocoderUrl, { cache: 'no-store' });

    if (localResult.ok) {
      const localResultData = await localResult.json() as GeocodeEarthResponse;

      geoDebugLog('geocoder_request_finish', {
        endpoint: 'autocomplete',
        request_id: requestId,
        status: localResult.status,
        ok: localResult.ok,
        features_count: localResultData.features.length,
        attempt: 'local',
      });

      // If local search returned results, use them
      if (localResultData.features && localResultData.features.length > 0) {
        return localResultData;
      }

      geoDebugLog('geocoder_fallback_triggered', {
        endpoint: 'autocomplete',
        request_id: requestId,
        reason: 'no_local_results',
        text_hash: hashForGeoDebug(searchPlaceName),
      });
    }
  }

  // Fallback: search WITHOUT focus point (global results)
  const globalRequestParameters: Record<string, string> = { ...geocodeEarthAutocompleteParameters, text: searchPlaceName };
  const globalUrlParameters = new URLSearchParams(globalRequestParameters).toString();
  const globalGeocoderUrl = `${config.autocompleteUrl}?${globalUrlParameters}`;

  geoDebugLog('geocoder_request_start', {
    endpoint: 'autocomplete',
    request_id: requestId,
    text_hash: hashForGeoDebug(searchPlaceName),
    has_focus_point: false,
    attempt: 'global',
  });

  const globalResult = await fetch(globalGeocoderUrl, { cache: 'no-store' });

  if (!globalResult.ok) {
    sendHttpError({
      baseName: 'Geocoder get autocomplete',
      functionName: 'autocompleteByName',
      method: 'GET',
      statusCode: globalResult.status,
      url: globalGeocoderUrl,
    });
  }

  const globalResultData = await globalResult.json() as GeocodeEarthResponse;

  geoDebugLog('geocoder_request_finish', {
    endpoint: 'autocomplete',
    request_id: requestId,
    status: globalResult.status,
    ok: globalResult.ok,
    features_count: globalResultData.features.length,
    attempt: 'global',
  });

  return globalResultData;
}

export async function searchPlaceByName(
  searchPlaceName: string,
  options?: { strategy?: 'default' | 'top' },
): Promise<Place | null> {
  const requestId = `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
  const requestParameters: Record<string, string> = { ...geocodeEarthSearchParameters, text: searchPlaceName };
  const urlParameters = new URLSearchParams(requestParameters).toString();
  const geocoderRequestUrl = `${config.forwardUrl}?${urlParameters}`;

  geoDebugLog('geocoder_request_start', {
    endpoint: 'search',
    request_id: requestId,
    text_hash: hashForGeoDebug(searchPlaceName),
    strategy: options?.strategy ?? 'default',
  });

  const result = await fetch(geocoderRequestUrl, { cache: 'no-store' });

  if (!result.ok) {
    sendHttpError({
      baseName: 'Geocoder search location by name',
      functionName: 'searchPlaceByName',
      method: 'GET',
      statusCode: result.status,
      url: geocoderRequestUrl,
    });
  }

  const searchData = await result.json() as GeocodeEarthResponse;
  const strategy = options?.strategy ?? 'default';
  const featureResponse = strategy === 'top'
    ? searchData.features[0]
    : geocodeFilterSearchResults(searchData.features, searchData.geocoding);

  geoDebugLog('geocoder_request_finish', {
    endpoint: 'search',
    request_id: requestId,
    status: result.status,
    ok: result.ok,
    strategy,
    features_count: searchData.features.length,
    selected_feature_hash: featureResponse ? hashForGeoDebug(featureResponse.properties.label) : null,
  });

  return featureResponse ? placeAdapter(featureResponse) : null;
}

export async function searchByCoordinates(coords: Coordinates) {
  const requestId = `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
  const requestParameters: Record<string, string> = {
    ...geocodeEarthSearchParameters,
    'point.lat': String(coords.latitude),
    'point.lon': String(coords.longitude),
    size: '1',
  };

  const urlParameters = new URLSearchParams(requestParameters).toString();
  const geocoderRequestUrl = `${config.reverseUrl}?${urlParameters}`;

  geoDebugLog('geocoder_request_start', {
    endpoint: 'reverse',
    request_id: requestId,
    coords_hash: hashForGeoDebug(`${coords.latitude},${coords.longitude}`),
  });

  const result = await fetch(geocoderRequestUrl, { cache: 'no-store' });

  if (!result.ok) {
    sendHttpError({
      baseName: 'Geocoder search location by coordinates',
      functionName: 'searchByCoordinates',
      method: 'GET',
      statusCode: result.status,
      url: geocoderRequestUrl,
    });
  }

  const resultData = await result.json() as GeocodeEarthResponse;

  geoDebugLog('geocoder_request_finish', {
    endpoint: 'reverse',
    request_id: requestId,
    status: result.status,
    ok: result.ok,
    features_count: resultData.features.length,
  });

  return {
    feature: resultData.features[0],
    geocoding: resultData.geocoding,
  };
}
